// ModuleDisabledScreen
//
// Rendered in place of a feature's screen when its PER-PLAN module (L3) is
// switched off for this plan. Sibling to FeatureDisabledScreen, and
// deliberately a different surface with different copy:
//
//   FeatureDisabledScreen  Eleplan turned this off for everyone. Nothing you
//                          can do; it will come back.
//   ModuleDisabledScreen   This plan has not turned this on. Somebody CAN turn
//                          it on, and this screen says who and how.
//
// That second half is the point. The whole feature exists so a new plan starts
// small, which only works if the features it did not start with stay
// discoverable — otherwise "not enabled yet" is indistinguishable from "does
// not exist", and a user with an Apple Watch never learns the app supports one.
// So this screen always offers a route to the catalog, and when the viewer can
// manage modules it says so plainly rather than making them go hunting.

import React from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../hooks/useTheme';
import { useDesktopContentMaxWidth } from '../utilities/desktopContent';
import { usePlanModuleState } from '../hooks/useModuleEnabled';
import Icon from './Icon';

interface Props {
  /**
   * Fallback display name. The label the server sent for this module wins when
   * the catalog has loaded, so copy stays in one place — the registry.
   */
  featureName?: string;
  /** Registry key, e.g. 'mod:vault'. Used to deep-link the catalog to this row. */
  moduleKey?: string;
  projectId?: string | number | null;
  /** Optional override for the body copy. */
  description?: string;
}

export default function ModuleDisabledScreen({
  featureName,
  moduleKey,
  projectId,
  description,
}: Props) {
  const theme = useTheme();
  const navigation = useNavigation<any>();
  const desktopContent = useDesktopContentMaxWidth();
  const slice = usePlanModuleState(projectId);

  // Prefer the registry's own label over whatever the call site guessed.
  const entry = moduleKey ? slice.modules.find((m) => m.key === moduleKey) : undefined;
  const name = entry?.label || featureName || 'This feature';

  const body =
    description
    || (slice.canManage
      ? `${name} is not turned on for this plan yet. You can turn it on from Features — nothing that was already collected is lost, it just becomes visible again.`
      : `${name} is not turned on for this plan yet. An owner or administrator can turn it on from Features.`);

  const openCatalog = React.useCallback(() => {
    if (!projectId) return;
    navigation.navigate('ProjectFeatures', {
      projectId,
      // Scrolls the catalog to this row and opens its sheet, so the user lands
      // on the thing they were trying to reach rather than a list of 23.
      highlightModule: moduleKey,
    });
  }, [navigation, projectId, moduleKey]);

  return (
    <SafeAreaView
      style={[styles.safe, { backgroundColor: theme.colors.background }]}
      edges={['top', 'left', 'right']}
    >
      <ScrollView
        contentContainerStyle={[styles.scroll, desktopContent]}
        showsVerticalScrollIndicator={false}
      >
        <View
          style={[
            styles.iconWrap,
            { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
          ]}
        >
          <Icon icon={['fad', 'eye-slash']} size={36} color={theme.colors.muted} />
        </View>

        <Text style={[styles.title, { color: theme.colors.text }]}>
          {name} is not on for this plan
        </Text>

        <Text style={[styles.body, { color: theme.colors.muted }]}>{body}</Text>

        {projectId ? (
          <TouchableOpacity
            style={[styles.cta, { backgroundColor: theme.colors.primary }]}
            onPress={openCatalog}
            accessibilityRole="button"
            accessibilityLabel={slice.canManage ? `Turn on ${name}` : 'See all features'}
          >
            <Text style={[styles.ctaText, { color: theme.colors.primaryText }]}>
              {slice.canManage ? `Turn on ${name}` : 'See all features'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  scroll: {
    padding: 24,
    paddingTop: 64,
    width: '100%',
    alignItems: 'center',
  },
  iconWrap: {
    width: 84,
    height: 84,
    borderRadius: 42,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 12,
  },
  body: {
    fontSize: 14,
    lineHeight: 21,
    textAlign: 'center',
    maxWidth: 400,
  },
  cta: {
    marginTop: 28,
    paddingHorizontal: 22,
    paddingVertical: 12,
    borderRadius: 24,
  },
  ctaText: {
    fontSize: 15,
    fontWeight: '600',
  },
});
